import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatusBadge } from "@/components/kit";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate } from "@/lib/api";
import { ProfileTab, PlansTab, LogsTab, CheckinsTab } from "@/pages/coach/ClientTabsA";
import { PhotosTab, HabitsTab, RemindersTab, SummaryTab, ClientBillingTab } from "@/pages/coach/ClientTabsB";

const TABS = [
  ["plans", "Plans", PlansTab], ["logs", "Logs", LogsTab],
  ["checkins", "Check-ins", CheckinsTab], ["photos", "Photos", PhotosTab], ["habits", "Habits", HabitsTab],
  ["reminders", "Reminders", RemindersTab], ["summary", "AI Summary", SummaryTab], ["billing", "Payments", ClientBillingTab],
  ["profile", "Profile", ProfileTab],
];

function timezoneLocale(timezone) {
  if (timezone.startsWith("America/Sao_Paulo")) return "pt-BR";
  if (timezone.startsWith("America/")) return "en-US";
  if (timezone.startsWith("Europe/")) return "en-GB";
  if (timezone.startsWith("Australia/")) return "en-AU";
  if (timezone.startsWith("Pacific/")) return "en-NZ";
  if (timezone.startsWith("Africa/")) return "en-ZA";
  if (["Asia/Kolkata", "Asia/Calcutta"].includes(timezone)) return "en-IN";
  if (timezone === "Asia/Tokyo") return "ja-JP";
  if (timezone === "Asia/Singapore") return "en-SG";
  if (timezone.startsWith("Asia/")) return "en-GB";
  return "en-GB";
}

function formatTimezone(timezone) {
  if (!timezone) return "";
  try {
    const locale = timezoneLocale(timezone);
    const abbreviation = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: "short" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value || "GMT";
    const fullName = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "long" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    const regionalName = fullName?.match(/^(.*?) (?:Standard|Daylight) Time$/);
    if (regionalName && /^[A-Z]{2,6}$/.test(abbreviation)) return `${regionalName[1]} (${abbreviation})`;
    return fullName || abbreviation;
  } catch {
    return timezone;
  }
}

function daysUntil(dateValue) {
  if (!dateValue) return null;
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  const endDay = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((endDay - today) / 86400000);
}

export default function ClientDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [client, reload] = useLoad(`/clients/${id}`);
  const [resending, setResending] = useState(false);
  const resendWelcome = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/clients/${id}/welcome-email`);
      if (data.welcome_email.status === "sent") toast.success("Welcome email sent");
      else toast.error(data.welcome_email.message || data.account.message || "Welcome email could not be sent");
      reload();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setResending(false);
    }
  };
  const requestedTab = params.get("tab");
  const tab = ["location", "notes"].includes(requestedTab) ? "profile" : TABS.some(([key]) => key === requestedTab) ? requestedTab : "plans";
  if (!client || Array.isArray(client)) return <div className="eyebrow">Loading client…</div>;
  const location = [client.city, client.state, client.country].filter(Boolean).join(", ");
  const locationDetails = [location, formatTimezone(client.timezone)].filter(Boolean).join(" · ") || "-";
  const planDaysLeft = daysUntil(client.end_date);
  return (
    <div>
      <PageHeader eyebrow={<span className="text-sm text-slate-400 normal-case tracking-normal break-words" style={{ fontFamily: "'DM Sans', sans-serif" }}>{`${fmtDate(client.start_date)} - ${fmtDate(client.end_date)}`}</span>} title={
        <span className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <span>{client.name}</span>
          <span className="flex flex-col items-start gap-2">
            <StatusBadge status={client.status} testid="client-membership-status" />
            <span className="text-sm font-medium normal-case tracking-normal text-slate-400">{client.package_name || "No plan"}{client.duration_weeks ? ` · ${client.duration_weeks} weeks` : ""}{planDaysLeft !== null ? ` · ${planDaysLeft < 0 ? "Ended" : `${planDaysLeft} ${planDaysLeft === 1 ? "day" : "days"} left`}` : ""}</span>
          </span>
        </span>
      } testid="client-detail-name" subtitle={<div className="mt-1 text-sm text-slate-400 break-words">{locationDetails}</div>} leading={
        <Button asChild type="button" variant="ghost" size="sm" data-testid="client-detail-back-btn">
          <Link to="/coach/clients" aria-label="Back to clients" title="Back to clients"><ArrowLeft size={16} className="mr-1" />Clients</Link>
        </Button>
      }>
        <Button type="button" variant="outline" size="sm" disabled={resending} onClick={resendWelcome} data-testid="client-resend-welcome-email">
          <Mail size={14} className="mr-1" />{resending ? "Sending…" : "Resend welcome email"}
        </Button>
      </PageHeader>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-[#111827] border border-[#1E293B] flex-wrap h-auto p-1 mb-6">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} className="rounded-full data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white" data-testid={`client-tab-${k}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([k, , C]) => (
          <TabsContent key={k} value={k}>{tab === k && <C client={client} reloadClient={reload} />}</TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
